import React, { useState, useMemo, useEffect } from 'react';
import { useEmailTemplates } from '@/hooks/useEmailTemplates';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Send, Eye, User, Mail, AlertCircle, Loader2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { Alert, AlertDescription } from '@/components/ui/alert';
import TemplateVariableEditor, { extractVariablesFromHtml } from './TemplateVariableEditor';

const EmailPreviewWithRealData = () => {
  const [selectedTemplateKey, setSelectedTemplateKey] = useState<string>('');
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [isSending, setIsSending] = useState(false);
  const [customVariables, setCustomVariables] = useState<Record<string, string>>({});
  
  const { templates, isLoading: templatesLoading } = useEmailTemplates();

  // Fetch users for dropdown
  const { data: users, isLoading: usersLoading, error: usersError } = useQuery({
    queryKey: ['admin-users-for-preview'],
    queryFn: async () => {
      const { data, error } = await supabase
        .rpc('get_admin_users_with_emails_paginated', {
          p_limit: 50,
          p_offset: 0,
          p_search_term: null,
          p_plan_types: null,
          p_gender: null,
          p_orientation: null,
          p_role: null,
          p_verification_level: null,
          p_onboarding_completed: true,
          p_hidden: null,
          p_created_after: null,
          p_created_before: null,
          p_user_type_filter: null,
          p_has_photos: null,
          p_user_roles: null,
        });
      
      if (error) throw error;
      return data || [];
    },
  });

  const selectedTemplate = useMemo(() => {
    return templates?.find(t => t.template_key === selectedTemplateKey);
  }, [templates, selectedTemplateKey]);

  const selectedUser = useMemo(() => {
    return users?.find(u => u.id === selectedUserId);
  }, [users, selectedUserId]);

  // Extract variables from template HTML
  const templateVariables = useMemo(() => {
    if (!selectedTemplate) return [];
    return extractVariablesFromHtml(selectedTemplate.html_content);
  }, [selectedTemplate]);

  // Get default variable values based on user
  const getDefaultVariables = (): Record<string, string> => {
    if (!selectedUser) return {};
    return {
      Name: selectedUser.username || 'Usuário',
      UserName: selectedUser.username || 'Usuário',
      Email: selectedUser.email || '',
      Username: selectedUser.username || '',
      ConfirmationURL: 'https://interlaced.app/confirm?token=PREVIEW_TOKEN',
      SiteURL: 'https://interlaced.app',
      SiteUrl: 'https://interlaced.app',
      Token: '123456',
      TokenHash: 'preview_hash_token',
      UnsubscribeUrl: `https://interlaced.app/unsubscribe?uid=${selectedUser.id}`,
      Date: new Date().toLocaleDateString('pt-BR'),
      PlanName: selectedUser.plan_type || 'Free',
      DaysRemaining: '7',
      ExpirationDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toLocaleDateString('pt-BR'),
      RenewalUrl: 'https://interlaced.app/main/subscriptions',
      LikesCount: '5',
      ViewsCount: '12',
      UnreadMessagesCount: '3',
      DaysInactive: '3',
      EngagementMessage: '5 pessoas curtiram você!',
      CtaText: 'Ver quem está interessado',
      NewProfilesCount: '10',
      MatchName: 'Maria',
      LikerName: 'João',
      SenderName: 'Pedro',
      MessagePreview: 'Olá! Como vai?',
      Reason: 'Motivo de exemplo',
    };
  };

  // Initialize variables when template or user changes
  useEffect(() => {
    if (selectedTemplate && selectedUser) {
      const defaults = getDefaultVariables();
      setCustomVariables(prev => {
        const merged = { ...defaults };
        // Keep existing custom values
        for (const key of templateVariables) {
          if (prev[key] !== undefined && prev[key] !== '') {
            merged[key] = prev[key];
          }
        }
        return merged;
      });
    }
  }, [selectedTemplateKey, selectedUserId]);

  const handleResetVariables = () => {
    setCustomVariables(getDefaultVariables());
  };

  const updateVariable = (key: string, value: string) => {
    setCustomVariables(prev => ({ ...prev, [key]: value }));
  };

  // Generate preview HTML with current variables
  const previewHtml = useMemo(() => {
    if (!selectedTemplate) return null;

    let html = selectedTemplate.html_content;
    
    // Replace all variables
    for (const [key, value] of Object.entries(customVariables)) {
      const patterns = [
        new RegExp(`\\{\\{\\s*\\.${key}\\s*\\}\\}`, 'g'),
        new RegExp(`\\{\\{\\s*${key}\\s*\\}\\}`, 'g'),
      ];
      patterns.forEach(pattern => {
        html = html.replace(pattern, value);
      });
    }

    return html;
  }, [selectedTemplate, customVariables]);

  const handleSendTest = async () => {
    if (!selectedTemplate || !selectedUser?.email) {
      toast.error('Selecione um template e um usuário');
      return;
    }

    setIsSending(true);
    try {
      const { error } = await supabase.functions.invoke('send-transactional-email', {
        body: {
          templateKey: selectedTemplate.template_key,
          toEmail: selectedUser.email,
          variables: customVariables,
          userId: selectedUser.id,
        },
      });

      if (error) throw error;
      toast.success(`Email enviado para ${selectedUser.email}`);
    } catch (error) {
      toast.error('Erro ao enviar email de teste');
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Error Alert */}
      {usersError && (
        <Alert variant="destructive" className="bg-red-500/10 border-red-500/30">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Erro ao carregar usuários: {usersError.message}
          </AlertDescription>
        </Alert>
      )}

      {/* Selection Controls */}
      <div className="bg-white/5 border border-white/10 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <Eye className="w-5 h-5 text-luxury-pinkBright" />
          Preview com Dados Reais
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Template Selector */}
          <div>
            <label className="text-sm text-white/60 mb-2 block">Template</label>
            <Select value={selectedTemplateKey} onValueChange={setSelectedTemplateKey}>
              <SelectTrigger className="bg-white/5 border-white/10 text-white">
                <SelectValue placeholder="Selecione um template" />
              </SelectTrigger>
              <SelectContent className="bg-gray-900 border-white/10 max-h-80">
                {templates?.map((t) => (
                  <SelectItem key={t.template_key} value={t.template_key} className="text-white">
                    <div className="flex items-center gap-2">
                      <span>{t.name}</span>
                      {t.is_active && (
                        <span className="text-xs text-green-400">(Ativo)</span>
                      )}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* User Selector */}
          <div>
            <label className="text-sm text-white/60 mb-2 block">Usuário (dados reais)</label>
            <Select value={selectedUserId} onValueChange={setSelectedUserId} disabled={usersLoading}>
              <SelectTrigger className="bg-white/5 border-white/10 text-white">
                {usersLoading ? (
                  <div className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>Carregando...</span>
                  </div>
                ) : (
                  <SelectValue placeholder="Selecione um usuário" />
                )}
              </SelectTrigger>
              <SelectContent className="bg-gray-900 border-white/10 max-h-60">
                {users?.map((u) => (
                  <SelectItem key={u.id} value={u.id} className="text-white">
                    <div className="flex items-center gap-2">
                      <User className="w-3 h-3 text-white/60" />
                      {u.username} 
                      <span className="text-white/40 text-xs">({u.email})</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Send Button */}
          <div className="flex items-end">
            <Button
              onClick={handleSendTest}
              disabled={!selectedTemplate || !selectedUser || isSending}
              className="bg-gradient-to-r from-[#C4406B] to-[#9F3764] text-white w-full"
            >
              <Send className="w-4 h-4 mr-2" />
              {isSending ? 'Enviando...' : 'Enviar para Usuário'}
            </Button>
          </div>
        </div>

        {/* Selected Info */}
        {selectedUser && (
          <div className="mt-4 p-3 bg-white/[0.03] rounded-lg flex items-center gap-3">
            <Mail className="w-4 h-4 text-luxury-pinkBright" />
            <span className="text-white/80 text-sm">
              Email será enviado para: <strong className="text-white">{selectedUser.email}</strong>
            </span>
          </div>
        )}
      </div>

      {/* Variable Editor */}
      {selectedTemplate && selectedUser && templateVariables.length > 0 && (
        <div className="bg-white/5 border border-white/10 rounded-xl p-6">
          <TemplateVariableEditor
            variables={templateVariables}
            values={customVariables}
            onChange={updateVariable}
            onResetToDefaults={handleResetVariables}
          />
        </div>
      )}

      {/* Preview */}
      {previewHtml ? (
        <div className="bg-white/5 border border-white/10 rounded-xl overflow-hidden">
          <div className="p-4 border-b border-white/10 flex items-center justify-between">
            <div>
              <h4 className="text-white font-medium">{selectedTemplate?.name}</h4>
              <p className="text-white/60 text-sm">
                Assunto: {selectedTemplate?.subject_template
                  ? Object.entries(customVariables).reduce(
                      (acc, [key, val]) => acc.replace(new RegExp(`\\{\\{\\s*\\.?${key}\\s*\\}\\}`, 'g'), val),
                      selectedTemplate.subject_template
                    )
                  : 'N/A'
                }
              </p>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleResetVariables}
              className="text-white/60 hover:text-white"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Resetar Variáveis
            </Button>
          </div>
          
          {/* Email Preview iFrame */}
          <div className="bg-white rounded-b-xl">
            <iframe
              srcDoc={previewHtml}
              title="Email Preview"
              className="w-full h-[600px] border-0"
              sandbox="allow-same-origin"
            />
          </div>
        </div>
      ) : (
        <div className="bg-white/5 border border-white/10 rounded-xl p-12 text-center">
          <Eye className="w-12 h-12 mx-auto mb-4 text-white/20" />
          <p className="text-white/60">
            Selecione um template e um usuário para visualizar o preview
          </p>
        </div>
      )}
    </div>
  );
};

export default EmailPreviewWithRealData;
